import { Navigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useCan, type OwnContext } from "@/hooks/useCan";
import { useAuthStore } from "@/stores/auth";
import type { RbacAction } from "@/lib/rbacMatrix";

export interface RoleGuardProps {
  action: RbacAction;
  context?: OwnContext;
  children: ReactNode;
  /** Where to send users that lack the capability. Defaults to `/`. */
  fallback?: string;
}

/**
 * Capability-gate for protected route subtrees. Redirects to /login when
 * unauthenticated, to `fallback` (`/` by default) otherwise.
 *
 * Server-side check is still authoritative (RBACError → 403 → toast); this
 * guard only avoids rendering UI a role would never be able to act on.
 */
export function RoleGuard({
  action,
  context,
  children,
  fallback = "/",
}: RoleGuardProps) {
  const userId = useAuthStore((s) => s.userId);
  const allowed = useCan(action, context);
  if (!userId) {
    return <Navigate to="/login" />;
  }
  if (!allowed) {
    return <Navigate to={fallback} />;
  }
  return <>{children}</>;
}
